import React from 'react';
import { useForm } from 'react-hook-form';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Swal from 'sweetalert2';

import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import UserNav from '../../components/navbar/user-nav';
import { useUser } from '../../context/UserContext';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';


// Schema de validación
const schema = yup.object().shape({
  nombre: yup.string().required('Nombre requerido'),
  descripcion: yup.string().required('Descripción requerida'),
  telefono: yup
    .string()
    .min(8, 'Mínimo 8 caracteres')
    .required('Teléfono requerido'),
  correo: yup.string().email('Correo inválido').required('Correo requerido'),
  direccion: yup
    .string()
    .url('Debe ser una URL válida')
    .required('URL requerida'),
  años_experiencia: yup
    .number()
    .typeError('Debe ser un número')
    .min(0, 'Mínimo 0 años')
    .required('Campo requerido'),
  redes: yup.object().shape({
    facebook: yup.string().url('URL inválida').notRequired(),
    instagram: yup.string().url('URL inválida').notRequired()
  })
});

type FormData = yup.InferType<typeof schema>;

export default function AddInmobiliaria() {
  const { user } = useUser();
const navigate = useNavigate();
const [autorizado, setAutorizado] = useState(false);
const [loading, setLoading] = useState(true);

useEffect(() => {
  if (user === undefined) return; // Todavía cargando el contexto

  if (!user || !user.tipo?.includes('admin')) {
    setAutorizado(false);
  } else {
    setAutorizado(true);
  }

  setLoading(false);
}, [user]);


  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      nombre: '',
      descripcion: '',
      telefono: '',
      correo: '',
      direccion: '',
      años_experiencia: 0,
      redes: {
        facebook: '',
        instagram: ''
      }
    }
  });

  const onSubmit = async (data: FormData) => {
    try {
      const nombreLower = data.nombre.trim().toLowerCase();
      const correoLower = data.correo.trim().toLowerCase();

      const qNombre = query(collection(db, 'inmobiliaria'), where('nombre', '==', nombreLower));
      const qCorreo = query(collection(db, 'inmobiliaria'), where('correo', '==', correoLower));

      const [snapNombre, snapCorreo] = await Promise.all([getDocs(qNombre), getDocs(qCorreo)]);

      if (!snapNombre.empty) {
        Swal.fire({
          icon: 'error',
          title: 'Nombre duplicado',
          text: 'Ya existe una inmobiliaria con ese nombre.',
          confirmButtonColor: '#d33'
        });
        return;
      }

      if (!snapCorreo.empty) {
        Swal.fire({
          icon: 'error',
          title: 'Correo duplicado',
          text: 'Ya existe una inmobiliaria con ese correo.',
          confirmButtonColor: '#d33'
        });
        return;
      }

      // ✅ Solo guardar datos del formulario + campos necesarios
      const nuevaInmobiliaria = {
        ...data,
        nombre: nombreLower,
        correo: correoLower,
        activo: true, // Por defecto activa
        foto: '', // Sin foto inicial
      };

      await addDoc(collection(db, 'inmobiliaria'), nuevaInmobiliaria);
      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Inmobiliaria registrada con éxito',
        confirmButtonColor: '#3085d6'
      });
      reset();
    } catch (error) {
      console.error('Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al registrar la inmobiliaria',
        confirmButtonColor: '#d33'
      });
    }
  };

  if (loading) return null;

if (!autorizado) {
  return (
    <div className="container py-5">
      <div className="alert alert-danger text-center shadow-sm rounded">
        <h4 className="mb-2">
          <i className="fa-solid fa-triangle-exclamation me-2"></i>
          Acceso denegado
        </h4>
        <p>No tienes permisos para registrar inmobiliarias.</p>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/dashboard')}>
          Regresar al Dashboard
        </button>
      </div>
    </div>
  );
}


  return (
    <>
      <style>{`
        .form-control::placeholder {
          color: #6c757d !important;
          opacity: 1 !important;
        }
        .form-control {
          color: #212529 !important;
        }
      `}</style>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <h2 className="ipt-title">Registrar Inmobiliaria</h2>
              <span className="ipn-subtitle">Completa el formulario para agregar una nueva inmobiliaria al sistema.</span>
            </div>
          </div>
        </div>
      </div>

      <section className="py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <form onSubmit={handleSubmit(onSubmit)} className="taller-form-modern">
                  {/* Sección 1: Información Básica */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">1. Información Básica</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Nombre <span className="text-danger">*</span></label>
                          <input
                            {...register('nombre')}
                            className="form-control"
                            placeholder="Ej. Homia"
                          />
                          <small className="text-danger">{errors.nombre?.message}</small>
                        </div>

                        <div className="form-group col-md-6">
                          <label>Correo <span className="text-danger">*</span></label>
                          <input
                            {...register('correo')}
                            type="email"
                            className="form-control"
                            placeholder="contacto@inmobiliaria.com"
                          />
                          <small className="text-danger">{errors.correo?.message}</small>
                        </div>

                        <div className="form-group col-md-6">
                          <label>Teléfono <span className="text-danger">*</span></label>
                          <input
                            {...register('telefono')}
                            className="form-control"
                            placeholder="7711234567"
                          />
                          <small className="text-danger">{errors.telefono?.message}</small>
                        </div>

                        <div className="form-group col-md-6">
                          <label>Años de experiencia <span className="text-danger">*</span></label>
                          <input
                            {...register('años_experiencia')}
                            type="number"
                            className="form-control"
                            placeholder="Ej. 5"
                          />
                          <small className="text-danger">
                            {errors.años_experiencia?.message}
                          </small>
                        </div>

                        <div className="form-group col-md-12">
                          <label>Descripción <span className="text-danger">*</span></label>
                          <textarea
                            {...register('descripcion')}
                            className="form-control h-120"
                            placeholder="Breve descripción de la inmobiliaria"
                          />
                          <small className="text-danger">
                            {errors.descripcion?.message}
                          </small>
                        </div>

                        <div className="form-group col-md-12">
                          <label>Dirección (URL) <span className="text-danger">*</span></label>
                          <input
                            {...register('direccion')}
                            className="form-control"
                            placeholder="https://maps.google.com/..."
                          />
                          <small className="text-danger">
                            {errors.direccion?.message}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 2: Redes Sociales */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">2. Redes Sociales</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>Facebook</label>
                          <input
                            {...register('redes.facebook')}
                            className="form-control"
                            placeholder="https://facebook.com/..."
                          />
                          <small className="text-danger">
                            {errors.redes?.facebook?.message}
                          </small>
                        </div>
                        <div className="form-group col-md-6">
                          <label>Instagram</label>
                          <input
                            {...register('redes.instagram')}
                            className="form-control"
                            placeholder="https://instagram.com/..."
                          />
                          <small className="text-danger">
                            {errors.redes?.instagram?.message}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botón de submit */}
                  <div className="text-center mt-4">
                    <button type="submit" className="btn btn-primary px-5">
                      <i className="fa fa-building me-2"></i>
                      Registrar Inmobiliaria
                      <i className="fa-solid fa-arrow-right-long ms-2"></i>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
